import axios from 'axios'

import { attachApiResponseLogger } from '@/lib/api/responseLogger'

import { resizeImageToJpeg } from './imageResize'

/**
 * 구장 사진 업로드 — Firebase Cloud Function(`imageUpdateV2`) 경유(설계 §2-1(a)·§2-3).
 *
 * 🚨 `facecock-was` 인증 클라이언트(`@/lib/api/client`)를 쓰지 않는다 — 대상 호스트가 다르고,
 * 이 Cloud Function 은 gym-admin 의 Bearer 토큰을 검증하지 않는다(엑셀 업로드 파이프라인이 S3
 * 직접 PUT 에 별도 raw 인스턴스를 쓴 것과 같은 이유 — `ADR-decision-presigned-async-upload`).
 *
 * 🚨 Firebase Auth·`firebase` npm 패키지에 의존하지 않는다 — 이 Cloud Function 은 Admin SDK 로
 * 스토리지에 써서 `storage.rules` 를 우회하므로 클라이언트 로그인이 필요 없다(모바일 앱의
 * `uploadImage2` 경로와 동일).
 */

const MAX_SOURCE_FILE_BYTES = 20 * 1024 * 1024

export class VenueImageUploadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'VenueImageUploadError'
  }
}

export interface VenueImageUploadResult {
  imageUrl: string
  thumbnailUrl: string | null
}

/** 대상 호스트가 달라 `client`(facecock-was 인증 인스턴스)와 분리한다. */
const uploadClient = axios.create({ timeout: 30_000 })

attachApiResponseLogger(uploadClient)

/** 업로드 전 사전 검증. 통과하면 `null`. */
export function validateVenueImageFile(file: File): string | null {
  if (!file.type.startsWith('image/')) return '이미지 파일만 업로드할 수 있습니다.'
  if (file.size > MAX_SOURCE_FILE_BYTES) return '파일 용량은 20MB를 넘을 수 없습니다.'
  return null
}

/** 모바일 `club/{clubId}/album-image` 컨벤션을 이식(설계 §2-3). */
export function venueImageReference(venueId: string): string {
  return `venue/${venueId}/photo`
}

/**
 * 지역 홍보 이미지 저장 참조(#732 설계 §자율 결정 8) — 구장 사진(`venue/{venueId}/photo`)과 스토리지
 * 경로가 섞이지 않게 분리한다. 업로드 파이프라인 자체는 `uploadVenueImage`를 그대로 재사용한다.
 */
export function venueCampaignImageReference(venueId: string): string {
  return `venue/${venueId}/campaign`
}

/** 모바일 `${clubId}_{epoch}_{index}.jpeg` 컨벤션과 같은 톤. 항상 `.jpg` — 업로드 전 JPEG 로 재인코딩하므로. */
export function venueImageFileName(venueId: string, index: number, now: number): string {
  return `${venueId}_${now}_${index}.jpg`
}

/**
 * Cloud Function 응답 봉투(`ApiResponse`, `firebase/functions/api/response/ApiResponse.js`).
 * 🚨 실제 JSON 키는 `returnCode`/`returnMessage`/`data` 다(Kotlin `FCResponse` 의 `@SerialName` 과 동일).
 */
interface CloudFunctionEnvelope {
  returnCode?: string
  returnMessage?: string
  data?: { originalUrl?: string; thumbnailUrl?: string }
}

const SUCCESS_CODE = '0000'

/**
 * 이미지 1장을 업로드하고 `{imageUrl, thumbnailUrl}` 을 돌려준다.
 * 실패(형식·용량 위반, 미설정 환경변수, 네트워크 오류, Cloud Function 오류)는 모두
 * `VenueImageUploadError` 로 던진다 — 호출부가 하나의 catch 로 처리할 수 있게.
 *
 * `reference` 생략 시 구장 사진 경로(`venueImageReference`)를 쓴다 — 지역 홍보 이미지는
 * `venueCampaignImageReference`를 넘겨 스토리지 경로를 분리한다(#732 설계 §자율 결정 8).
 */
export async function uploadVenueImage(
  file: File,
  venueId: string,
  index: number,
  reference: string = venueImageReference(venueId)
): Promise<VenueImageUploadResult> {
  const validationError = validateVenueImageFile(file)
  if (validationError) throw new VenueImageUploadError(validationError)

  const uploadUrl = import.meta.env.VITE_FIREBASE_UPLOAD_URL?.trim()
  if (!uploadUrl) throw new VenueImageUploadError('이미지 업로드가 설정되지 않았습니다.')

  const jpegBlob = await resizeImageToJpeg(file)
  const name = venueImageFileName(venueId, index, Date.now())

  const formData = new FormData()
  formData.append('reference', reference)
  formData.append('name', name)
  formData.append('file', jpegBlob, name)

  let envelope: CloudFunctionEnvelope
  try {
    const response = await uploadClient.post<CloudFunctionEnvelope>(uploadUrl, formData)
    envelope = response.data
  } catch {
    throw new VenueImageUploadError('이미지를 업로드하지 못했습니다. 잠시 후 다시 시도해 주세요.')
  }

  if (envelope.returnCode !== SUCCESS_CODE || !envelope.data?.originalUrl) {
    throw new VenueImageUploadError(envelope.returnMessage || '이미지를 업로드하지 못했습니다.')
  }

  const thumbnailUrl = envelope.data.thumbnailUrl
  return {
    imageUrl: envelope.data.originalUrl,
    thumbnailUrl: thumbnailUrl && thumbnailUrl.length > 0 ? thumbnailUrl : null,
  }
}
